import * as ToastPrimitive from '@radix-ui/react-toast'
import { X } from 'lucide-react'

import { tt } from '@agos/shared'

import { cn } from '../cn'
import { useToast } from './use-toast'

/**
 * The toast renderer, ported from legacy `components/ui/{toast,toaster}.tsx`.
 *
 * Placement is ported exactly — top of the screen on phones, bottom-right from
 * `sm` up — because staff learn where to look for "Storno vykonané" and moving
 * it costs them a glance on every order.
 */

const TOAST_BASE_CLASSES =
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all ' +
  'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] ' +
  'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none ' +
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out ' +
  'data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full ' +
  'data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full'

const TOAST_VARIANT_CLASSES = {
  default: 'border bg-background text-foreground',
  destructive: 'destructive group border-destructive bg-destructive text-destructive-foreground',
} as const

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastPrimitive.Provider>
      {toasts.map(({ id, title, description, variant, duration, open, onOpenChange }) => (
        <ToastPrimitive.Root
          key={id}
          open={open}
          duration={duration}
          onOpenChange={onOpenChange}
          // Radix also renders the same text into an off-screen live region for screen readers, so a
          // test asking for the words alone matches two elements and fails on strict mode. This is the
          // toast itself.
          data-toast={variant ?? 'default'}
          className={cn(TOAST_BASE_CLASSES, TOAST_VARIANT_CLASSES[variant ?? 'default'])}
        >
          <div className="grid gap-1">
            {title && (
              <ToastPrimitive.Title className="text-sm font-semibold">
                {title}
              </ToastPrimitive.Title>
            )}
            {description && (
              <ToastPrimitive.Description className="text-sm opacity-90">
                {description}
              </ToastPrimitive.Description>
            )}
          </div>
          <ToastPrimitive.Close
            aria-label={tt('Zatvoriť')}
            className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none group-hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]" />
    </ToastPrimitive.Provider>
  )
}
